import React, { useEffect, useState } from "react";
import "./CustomSortDialog.css";

/**
 * Stacked-letter sort icon (matches Excel's A→Z / Z→A glyphs):
 * two letters with a downward arrow.  `desc` flips A/Z order.
 */
function SortGlyph({ desc }) {
  const top = desc ? "Z" : "A";
  const bottom = desc ? "A" : "Z";
  return (
    <svg className="sev-csort-glyph" viewBox="0 0 20 20" aria-hidden="true">
      <text x="0" y="8" className="sev-csort-glyph-letter">{top}</text>
      <text x="0" y="18" className="sev-csort-glyph-letter">{bottom}</text>
      <path d="M15 3 L15 14 M12 11 L15 14.5 L18 11" className="sev-csort-glyph-arrow" />
    </svg>
  );
}

/**
 * CustomSortDialog
 * Excel-style multi-level sort.  The user adds sort levels (column +
 * order); rows are sorted by the first level, ties broken by the next,
 * and so on.  Applying calls onApply with the ordered [{ col, dir }].
 *
 * This is a presentational modal — all sort state lives in useSort; the
 * dialog only edits a working copy seeded from the current levels.
 */
function CustomSortDialog({ open, columns, initialLevels, initialHasHeaderRow, onApply, onClose }) {
  const [levels, setLevels] = useState([]);
  const [hasHeader, setHasHeader] = useState(true);

  // Seed the working copy whenever the dialog opens.
  useEffect(() => {
    if (!open) return;
    const seed = (initialLevels && initialLevels.length)
      ? initialLevels.map((l) => ({ col: l.col, dir: l.dir }))
      : [{ col: columns[0] ? columns[0].value : 0, dir: "asc" }];
    setLevels(seed);
    setHasHeader(initialHasHeaderRow !== false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on Escape for a more polished feel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const labelFor = (c) => (hasHeader && c.header ? c.header : c.letter);

  const usedCols = new Set(levels.map((l) => l.col));
  const firstUnused = columns.find((c) => !usedCols.has(c.value));
  const canAdd = levels.length < columns.length;

  const addLevel = () => {
    const col = firstUnused ? firstUnused.value : (columns[0] ? columns[0].value : 0);
    setLevels((prev) => [...prev, { col, dir: "asc" }]);
  };
  const removeLevel = (i) => setLevels((prev) => prev.filter((_, idx) => idx !== i));
  const moveLevel = (i, delta) => setLevels((prev) => {
    const j = i + delta;
    if (j < 0 || j >= prev.length) return prev;
    const next = [...prev];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const setCol = (i, col) => setLevels((prev) => prev.map((l, idx) => (idx === i ? { ...l, col } : l)));
  const setDir = (i, dir) => setLevels((prev) => prev.map((l, idx) => (idx === i ? { ...l, dir } : l)));

  const apply = () => { onApply(levels.filter((l) => l.col != null), hasHeader); onClose(); };

  // Reset to default = CLEAR the sort: applying empty levels removes all
  // sorting so the data returns to its original (pre-sort) order, then close.
  const resetDefaults = () => {
    onApply([], hasHeader);
    onClose();
  };

  const colLabel = (val) => {
    const c = columns.find((x) => x.value === val);
    return c ? labelFor(c) : "";
  };
  const summary = levels.length
    ? levels.map((l, i) => `${i ? "then " : ""}${colLabel(l.col)} (${l.dir === "desc" ? "Z→A" : "A→Z"})`).join(", ")
    : "No sort levels";

  return (
    <div className="sev-csort-backdrop" onMouseDown={onClose}>
      <div className="sev-csort-dialog" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="Custom Sort" aria-modal="true">
        <div className="sev-csort-header">
          <span className="sev-csort-header-icon"><span className="material-icons">swap_vert</span></span>
          <div className="sev-csort-header-text">
            <span className="sev-csort-title">Custom Sort</span>
            <span className="sev-csort-subtitle">Sort by one or more columns in priority order</span>
          </div>
          <button type="button" className="sev-csort-close" onClick={onClose} aria-label="Close">
            <span className="material-icons">close</span>
          </button>
        </div>

        <div className="sev-csort-toolbar">
          <button type="button" className="sev-csort-add" onClick={addLevel} disabled={!canAdd}>
            <span className="material-icons">add</span> Add level
          </button>
          <label className="sev-csort-headers" title="When on, the first row is treated as column headers and stays fixed while sorting">
            <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
            <span className="sev-csort-switch" aria-hidden="true"></span>
            <span>My data has headers</span>
          </label>
        </div>

        <div className="sev-csort-levels">
          {levels.map((lvl, i) => (
            <div className="sev-csort-row" key={i}>
              <span className="sev-csort-badge" title={i === 0 ? "Primary sort" : `Tie-breaker ${i}`}>{i + 1}</span>

              <div className="sev-csort-main">
                <span className="sev-csort-rowlabel">{i === 0 ? "Sort by" : "Then by"}</span>
                <div className="sev-csort-selectwrap">
                  <select className="sev-csort-select" value={lvl.col} onChange={(e) => setCol(i, Number(e.target.value))}>
                    {columns.map((c) => (
                      <option key={c.value} value={c.value}>{labelFor(c)}</option>
                    ))}
                  </select>
                  <span className="material-icons sev-csort-caret">expand_more</span>
                </div>

                <div className="sev-csort-dir" role="group" aria-label="Sort order">
                  <button
                    type="button"
                    className={"sev-csort-dirbtn" + (lvl.dir === "asc" ? " is-active" : "")}
                    onClick={() => setDir(i, "asc")}
                    aria-pressed={lvl.dir === "asc"}
                    title="Sort Ascending (A → Z)"
                  >
                    <SortGlyph />
                    <span className="sev-csort-dirlabel">Ascending</span>
                  </button>
                  <button
                    type="button"
                    className={"sev-csort-dirbtn" + (lvl.dir === "desc" ? " is-active" : "")}
                    onClick={() => setDir(i, "desc")}
                    aria-pressed={lvl.dir === "desc"}
                    title="Sort Descending (Z → A)"
                  >
                    <SortGlyph desc />
                    <span className="sev-csort-dirlabel">Descending</span>
                  </button>
                </div>
              </div>

              <div className="sev-csort-actions">
                <button type="button" className="sev-csort-iconbtn" title="Move up" onClick={() => moveLevel(i, -1)} disabled={i === 0}>
                  <span className="material-icons">arrow_upward</span>
                </button>
                <button type="button" className="sev-csort-iconbtn" title="Move down" onClick={() => moveLevel(i, 1)} disabled={i === levels.length - 1}>
                  <span className="material-icons">arrow_downward</span>
                </button>
                <button type="button" className="sev-csort-iconbtn sev-csort-del" title="Delete level" onClick={() => removeLevel(i)} disabled={levels.length <= 1}>
                  <span className="material-icons">delete_outline</span>
                </button>
              </div>
            </div>
          ))}

          {!canAdd && (
            <div className="sev-csort-hint">All columns are in use.</div>
          )}
        </div>

        <div className="sev-csort-summary" title={summary}>
          <span className="material-icons">info</span>
          <span className="sev-csort-summary-text">{summary}</span>
        </div>

        <div className="sev-csort-footer">
          <button type="button" className="sev-csort-reset" onClick={resetDefaults} title="Reset the sort options to default">
            <span className="material-icons">restart_alt</span> Reset to default
          </button>
          <div className="sev-csort-footer-actions">
            <button type="button" className="sev-csort-cancel" onClick={onClose}>Cancel</button>
            <button type="button" className="sev-csort-ok" onClick={apply} disabled={levels.length === 0}>
              <span className="material-icons">check</span> Apply Sort
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomSortDialog;
